import * as React from "react";
import type { ReactNode, HTMLAttributes } from "react";
import { cva } from "cva";
import { cn } from "@repo/cn";

type AlertType = "success" | "danger" | "info" | "warning" | "primary";

export interface CpsAlertV2Props {
  type: AlertType;
  htmlProps?: HTMLAttributes<HTMLElement>;
  children?: ReactNode;
  hover?: boolean;
}

const styleClass = cva({
  variants: {
    bg: {
      success: "bg-success-50",
      danger: "bg-danger-50",
      info: "bg-secondary-50",
      warning: "bg-warning-50",
      primary: "bg-primary-50",
      default: "",
    },
    hover: {
      success: "cursor-pointer hover:bg-success-100",
      danger: "cursor-pointer hover:bg-danger-100",
      info: "cursor-pointer hover:bg-secondary-100",
      warning: "cursor-pointer hover:bg-warning-100",
      primary: "cursor-pointer hover:bg-primary-100",
      default: "",
    },
    text: {
      success: "text-success-700",
      danger: "text-danger-700",
      info: "text-secondary-500",
      warning: "text-warning-700",
      primary: "text-primary-900",
      default: "",
    },
  },
  defaultVariants: {
    bg: "default",
    hover: "default",
    text: "default",
  },
});

export const CpsAlertV2Root = React.forwardRef<
  React.HTMLAttributes<HTMLElement>,
  CpsAlertV2Props
>(({ type = "info", hover = false, children, htmlProps }) => {
  const hoverStyleKey = hover ? type : "default";
  const rootStyleClass = styleClass({
    bg: type,
    hover: hoverStyleKey,
  });

  return (
    <div
      {...htmlProps}
      className={cn(
        "flex w-full items-center justify-start rounded-md p-4",
        rootStyleClass,
        htmlProps?.className,
      )}
    >
      {children}
    </div>
  );
});
CpsAlertV2Root.displayName = "Alert";

export const CpsAlertV2IconArea = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ children }) => <div className="h-6 w-6">{children}</div>);
CpsAlertV2IconArea.displayName = "AlertIconArea";

export interface CpsAlertTitleProps {
  type?: AlertType;
  children: ReactNode;
}
CpsAlertV2IconArea.displayName = "AlertIconArea";

export const CpsAlertV2TextArea = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ children }) => <div className="ml-2 flex flex-col justify-center">{children}</div>);
CpsAlertV2TextArea.displayName = "AlertTextArea";

export interface CpsAlertTitleProps {
  type?: AlertType;
  children: ReactNode;
}

export const CpsAlertV2Title = React.forwardRef<HTMLParagraphElement, CpsAlertTitleProps>(
  ({ type = "info", children }) => {
    const titleStyleClass = styleClass({
      text: type,
    });

    return <p className={cn("font-medium", titleStyleClass)}>{children}</p>;
  },
);
CpsAlertV2Title.displayName = "AlertTitle";

export const CpsAlertV2Description = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ children }) => <p className="text-sm font-normal text-neutral-500">{children}</p>);
CpsAlertV2Description.displayName = "AlertDescription";
